import React, { useState, useEffect } from 'react';
import { Box, TextField, Grid, Typography, RadioGroup, FormControlLabel, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Collapse, IconButton, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ncdAPI } from "../../../../services/ncd.api";

// No refs needed - this is a pure presentation component
const VitalsForm = ({ individualId, gender }) => {
  const patientGender = gender || (() => {
    try {
      const stored = localStorage.getItem("beneficiaryDetails");
      if (stored) {
        return JSON.parse(stored).gender;
      }
    } catch (e) {
      console.error("Error reading beneficiaryDetails from localStorage", e);
    }
    return "";
  })();

  const isFemale = ["female", "f"].includes((patientGender || "").trim().toLowerCase());

  const { values } = useFormikContext();
  const [showHistory, setShowHistory] = useState(true);
  const [expandHistory, setExpandHistory] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [vitalsHistory, setVitalsHistory] = useState([]);

  const validatePostPrandial = (value) => {
    const rbs = values.vitals?.randomBloodSugar;
    if ((value === undefined || value === null || value === '') && 
        (rbs === undefined || rbs === null || rbs === '')) {
      return 'Post Prandial Blood Sugar must be filled';
    }
    return undefined;
  };

  const validateRandom = (value) => {
    const ppbs = values.vitals?.postPrandialBloodSugar;
    if ((value === undefined || value === null || value === '') && 
        (ppbs === undefined || ppbs === null || ppbs === '')) {
      return 'Random Blood Sugar must be filled';
    }
    return undefined;
  };

  const validatePostPrandialType = (value) => {
    const ppbs = values.vitals?.postPrandialBloodSugar;
    if (ppbs && ppbs !== '' && (value === undefined || value === null || value === '')) {
      return 'Please select blood sugar type';
    }
    return undefined;
  };

  const validateRandomType = (value) => {
    const rbs = values.vitals?.randomBloodSugar;
    if (rbs && rbs !== '' && (value === undefined || value === null || value === '')) {
      return 'Please select blood sugar type';
    }
    return undefined;
  };

  const transformVitalsData = (apiResponse) => {
    if (!apiResponse) return [];

    const transformedData = [];

    Object.entries(apiResponse).forEach(([date, vitalsArray]) => {
      vitalsArray.forEach((vital) => {
        let row = {
          date: vital.bpVitals?.assesDate || vital.measurementVitals?.assesDate || vital.sugarVitals?.assesDate || date,
          updatedBy: vital.userId || '-',
          facilityTypeId: vital.facilityTypeId,
          bpReadings: {
            systolic: vital.bpVitals?.sys || null,
            diastolic: vital.bpVitals?.diast || null,
            heartRate: vital.bpVitals?.heartRate || null,
            unit: 'mmHg'
          },
          bloodSugar: {
            rbs: vital.sugarVitals?.rndBldGlc || null,
            PPBS: vital.sugarVitals?.postPradBldGlc || null,
            fbs: vital.sugarVitals?.fastngBldGlc || null,
            hba1c: vital.sugarVitals?.hba1c || null,
            unit: 'mg/dl'
          },
          measurements: {
            height: vital.measurementVitals?.height || null,
            weight: vital.measurementVitals?.weight || null,
            bmi: vital.measurementVitals?.bmi || null,
            waistCircumference: vital.measurementVitals?.wstCirmfnc || null
          }
        };

        transformedData.push(row);
      });
    });

    return transformedData.sort((a, b) => {
      const dateA = new Date(a.date.split('-').reverse().join('-'));
      const dateB = new Date(b.date.split('-').reverse().join('-'));
      return dateB - dateA;
    });
  };

  const fetchVitals = async () => {
    try {
      setIsPageLoading(true);
      if (!individualId) return;

      const vitalsData = await ncdAPI.getVitalsHistory(individualId);
      const transformedVitals = transformVitalsData(vitalsData?.data);
      setVitalsHistory(transformedVitals);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    fetchVitals();
  }, [individualId]);

  return (
    <Box className="form-section">
      <Box className="vitals-history-section" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Vitals History</Typography>
          <IconButton size="small" onClick={() => setShowHistory(!showHistory)}>
            {showHistory ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        <Collapse in={showHistory}>
          {vitalsHistory.length > 0 ? (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Updated by</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>BP Readings</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Blood Sugar Levels</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Measurements</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(expandHistory ? vitalsHistory : vitalsHistory.slice(0, 2)).map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.updatedBy}</TableCell>
                        <TableCell>
                          {row.bpReadings.systolic ? (
                            <Box>
                              <Typography variant="body2"><span className='label-style'>BP:</span> <b>{row.bpReadings.systolic}/{row.bpReadings.diastolic}</b> mmHg</Typography>
                              {row.bpReadings.heartRate && (
                              <Typography variant="body2"><span className='label-style'>Heart Rate:</span> <b>{row.bpReadings.heartRate}</b> bpm</Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.bloodSugar? (
                            <Box>
                              {row.bloodSugar.fbs && (
                              <Typography variant="body2"><span className='label-style'>FBS:</span> <b>{row.bloodSugar.fbs}</b> mg/dl</Typography>
                              )}
                              {row.bloodSugar.PPBS && (
                              <Typography variant="body2"><span className='label-style'>PPBS:</span> <b>{row.bloodSugar.PPBS}</b> mg/dl</Typography>
                              )}
                              {row.bloodSugar.rbs && (
                              <Typography variant="body2"><span className='label-style'>RBS:</span> <b>{row.bloodSugar.rbs}</b> mg/dl</Typography>
                              )}

                              {row.bloodSugar.hba1c && (
                                <Typography variant="body2"><span className='label-style'>HbA1c:</span> <b>{row.bloodSugar.hba1c}</b> %</Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.measurements.height || row.measurements.weight || row.measurements.bmi || row.measurements.waistCircumference ? (
                            <Box>
                              {row.measurements.height && (
                                <Typography variant="body2"> <span className='label-style'>Height:</span> <b>{row.measurements.height}</b> cms</Typography>
                              )}
                              {row.measurements.weight && (
                                <Typography variant="body2"> <span className='label-style'>Weight:</span> <b>{row.measurements.weight}</b> kgs</Typography>
                              )}
                              {row.measurements.bmi && (
                                <Typography variant="body2"> <span className='label-style'>BMI:</span> <b>{row.measurements.bmi}</b></Typography>
                              )}
                              {row.measurements.waistCircumference && (
                                <Typography variant="body2"> <span className='label-style'>Waist Circumference:</span> <b>{row.measurements.waistCircumference}</b></Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                {vitalsHistory.length > 2 && (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer', textDecoration: 'underline', mb: 1 }}
                    onClick={() => setExpandHistory(!expandHistory)}
                  >
                    {expandHistory ? 'SHOW LESS ↑' : 'SHOW MORE ↓'}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {isPageLoading ? 'Loading vitals history...' : 'No vitals history available'}
            </Typography>
          )}
        </Collapse>
      </Box>

      <Typography variant="h6" className="section-title">Vitals</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Field name="date">
              {({ field, form }) => (
                <DatePicker
                  label="Date"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : dayjs()}
                  onChange={(newValue) => {
                    form.setFieldValue('date', newValue ? newValue.format('YYYY-MM-DD') : '');
                  }}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal'
                    }
                  }}
                />
              )}
            </Field>
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Box className="subsection">
        <Box className="subsection-header">
          <img src="/images/bp_readings.png" alt="BP" className="section-icon" />
          <Typography variant="subtitle1">BP Readings</Typography>
        </Box>
        <Grid container spacing={2} sx={{ display: 'flex', width: '100%' }}>
          <Grid item xs={12} sm={4} sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Field name="vitals.systolicBP">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  label="Systolic blood pressure"
                  placeholder="mmHg"
                  fullWidth
                  type="number"
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                />
              )}
            </Field>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Field name="vitals.diastolicBP">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  label="Diastolic blood pressure"
                  placeholder="mmHg"
                  fullWidth
                  type="number"
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                />
              )}
            </Field>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Field name="vitals.heartRate">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  label="Heart Rate"
                  placeholder="BPM"
                  fullWidth
                  type="number"
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                />
              )}
            </Field>
          </Grid>
        </Grid>
      </Box>

      <Box className="subsection">
        <Box className="subsection-header">
          <img src="/images/bp_readings.png" alt="Blood Sugar" className="section-icon" />
          <Typography variant="subtitle1">Blood Sugar Levels</Typography>
        </Box>
        <Grid container spacing={2} sx={{ display: 'flex', width: '100%' }}>
          <Grid item xs={12} sm={4} sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Field name="vitals.fastingBloodSugar">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  label="Fasting Blood Sugar"
                  placeholder="mg/dl"
                  fullWidth
                  type="number"
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                />
              )}
            </Field>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Field name="vitals.postPrandialBloodSugar" validate={validatePostPrandial}>
              {({ field, form, meta }) => (
                <TextField
                  {...field}
                  label="Post Prandial Blood Sugar"
                  placeholder="mg/dl"
                  fullWidth
                  type="number"
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!e.target.value || e.target.value === '') {
                      form.setFieldValue('vitals.postPrandialBloodSugarType', '');
                    }
                  }}
                />
              )}
            </Field>
            <Field name="vitals.postPrandialBloodSugarType" validate={validatePostPrandialType}>
              {({ field, form, meta }) => {
                const postPrandialValue = form.values.vitals?.postPrandialBloodSugar;
                const isEnabled = postPrandialValue && postPrandialValue !== '';

                return (
                  <Box>
                    <RadioGroup
                      value={field.value || ''}
                      onChange={(e) => {
                        form.setFieldValue('vitals.postPrandialBloodSugarType', e.target.value);
                      }}
                      row
                      sx={{
                        opacity: isEnabled ? 1 : 0.5,
                        pointerEvents: isEnabled ? 'auto' : 'none'
                      }}
                    >
                      <FormControlLabel value="ppbgCapillary" control={<Radio />} label="Capillary" disabled={!isEnabled} />
                      <FormControlLabel value="ppbgVenous" control={<Radio />} label="Venous" disabled={!isEnabled} />
                    </RadioGroup>
                    {meta.touched && meta.error && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {meta.error}
                      </Typography>
                    )}
                  </Box>
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Field name="vitals.randomBloodSugar" validate={validateRandom}>
              {({ field, form, meta }) => (
                <TextField
                  {...field}
                  label="Random Blood Sugar"
                  placeholder="mg/dl"
                  fullWidth
                  type="number"
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!e.target.value || e.target.value === '') {
                      form.setFieldValue('vitals.randomBloodSugarType', '');
                    }
                  }}
                />
              )}
            </Field>
            <Field name="vitals.randomBloodSugarType" validate={validateRandomType}>
              {({ field, form, meta }) => {
                const randomBloodSugarValue = form.values.vitals?.randomBloodSugar;
                const isEnabled = randomBloodSugarValue && randomBloodSugarValue !== '';

                return (
                  <Box>
                    <RadioGroup
                      value={field.value || ''}
                      onChange={(e) => {
                        form.setFieldValue('vitals.randomBloodSugarType', e.target.value);
                      }}
                      row
                      sx={{
                        opacity: isEnabled ? 1 : 0.5,
                        pointerEvents: isEnabled ? 'auto' : 'none'
                      }}
                    >
                      <FormControlLabel value="rbgCapillary" control={<Radio />} label="Capillary" disabled={!isEnabled} />
                      <FormControlLabel value="rbgVenous" control={<Radio />} label="Venous" disabled={!isEnabled} />
                    </RadioGroup>
                    {meta.touched && meta.error && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {meta.error}
                      </Typography>
                    )}
                  </Box>
                );
              }}
            </Field>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Field name="vitals.hba1c">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  label="HbA1c"
                  placeholder="%"
                  fullWidth
                  type="number"
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                />
              )}
            </Field>
          </Grid>
        </Grid>
      </Box>

      <Box className="subsection">
        <Box className="subsection-header">
          <img src="/images/measurement-of-waist.png" alt="Measurements" className="section-icon" />
          <Typography variant="subtitle1">Measurements</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} sx={{ flex: 1 }}>
            <Field name="vitals.height">
              {({ field, form, meta }) => {
                const calculateBMI = (height, weight) => {
                  if (height && weight && height > 0 && weight > 0) {
                    const heightInMeters = height / 100;
                    const bmi = weight / (heightInMeters * heightInMeters);
                    return bmi.toFixed(1);
                  }
                  return '';
                };

                return (
                  <TextField
                    {...field}
                    label="Height"
                    placeholder="cms"
                    fullWidth
                    type="number"
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                    onChange={(e) => {
                      field.onChange(e);
                      const height = e.target.value;
                      const weight = form.values.vitals?.weight;
                      const bmi = calculateBMI(height, weight);
                      if (bmi) {
                        form.setFieldValue('vitals.bmi', bmi);
                      }
                    }}
                  />
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ flex: 1 }}>
            <Field name="vitals.weight">
              {({ field, form, meta }) => {
                const calculateBMI = (height, weight) => {
                  if (height && weight && height > 0 && weight > 0) {
                    const heightInMeters = height / 100;
                    const bmi = weight / (heightInMeters * heightInMeters);
                    return bmi.toFixed(1);
                  }
                  return '';
                };

                return (
                  <TextField
                    {...field}
                    label="Weight"
                    placeholder="kgs"
                    fullWidth
                    type="number"
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                    onChange={(e) => {
                      field.onChange(e);
                      const weight = e.target.value;
                      const height = form.values.vitals?.height;
                      const bmi = calculateBMI(height, weight);
                      if (bmi) {
                        form.setFieldValue('vitals.bmi', bmi);
                      }
                    }}
                  />
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ flex: 1 }}>
            <Field name="vitals.bmi">
              {({ field }) => (
                <TextField
                  {...field}
                  label="BMI"
                  placeholder="kg/m2"
                  fullWidth
                  type="number"
                  InputProps={{ readOnly: true }}
                />
              )}
            </Field>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ flex: 1 }}>
            <Field name="vitals.waistCircumference">
              {({ field, form }) => (
                <FormControl fullWidth>
                  <InputLabel>Waist Circumference</InputLabel>
                  <Select
                    {...field}
                    label="Waist Circumference"
                    displayEmpty
                    value={field.value || ''}
                    onChange={(e) => {
                      form.setFieldValue('vitals.waistCircumference', e.target.value);
                    }}
                  >
                    {isFemale ? (
                      <>
                        <MenuItem value="80 cm or less">80 cm or less</MenuItem>
                        <MenuItem value="81-90 cm">81-90 cm</MenuItem>
                        <MenuItem value="more than 90 cm">More than 90 cm</MenuItem>
                      </>
                    ) : (
                      <>
                        <MenuItem value="90 cm or less">90 cm or less</MenuItem>
                        <MenuItem value="91-100 cm">91-100 cm</MenuItem>
                        <MenuItem value="more than 100 cm">More than 100 cm</MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              )}
            </Field>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default VitalsForm;
